import { api } from "Requests/Api";
import { Button, Modal, Select, message } from "antd";
import { AxiosError } from "axios";
import { Conversation, ConversationGroup } from "frontend-lib/DomainTypes/Conversation";
import { isMobileView } from "frontend-lib/Utils/Mobile";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import CTA from "../../Constants";
import { useAppContext } from "../../State/Global/AppContext";
import {
    IonButton,
    IonButtons,
    IonContent,
    IonItem,
    IonLabel,
    IonModal,
    IonSelect,
    IonSelectOption,
    IonSpinner,
    IonTitle,
    IonToolbar,
} from "@ionic/react";

const { Option } = Select;

type Props = {
    visible: boolean;
    selectedChat?: Conversation;
    groups: Array<ConversationGroup>;
    loadChatsCb: () => Promise<void>;
    handleCloseModalCb: () => void;
};

export default function SelectGroupModal({
    visible,
    selectedChat,
    loadChatsCb,
    handleCloseModalCb,
    groups,
}: Props) {
    const { t } = useTranslation();
    const [acceptButtonDisabled, setAcceptButtonDisabled] = useState(false);
    const [selectedGroupsId, setSelectedGroupsId] = useState<Array<number>>([]);
    //@ts-ignore
    const { appState } = useAppContext();
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (!selectedChat) return;

        const getDefaultGroups = (chatId: number): ConversationGroup[] => {
            const chatFound: Conversation | undefined = appState.chats.find(
                (c: Conversation) => c.id === chatId
            );

            if (!chatFound) return [];
            return chatFound.groups;
        };
        const defaultGroupsForChat = getDefaultGroups(selectedChat.id);
        setSelectedGroupsId(defaultGroupsForChat.map((group) => group.id));
    }, [appState.chats, selectedChat, visible]);

    const handleAssignError = (err: string) => {
        let errorMsg;
        if (err === CTA.CHATS.ERRORS.GROUP_NOT_ASSIGNED) {
            errorMsg = t("groupAssignedError");
        } else if (err === CTA.CHATS.ERRORS.GROUP_NOT_BELONGS) {
            errorMsg = t("groupNotBelongsError");
        } else {
            errorMsg = t("assignError");
        }
        message.error(errorMsg);
    };

    const handleModalOk = async () => {
        try {
            setIsLoading(true);
            const groups = selectedGroupsId.map((group) => ({ id: group }));
            await api.chats().updateChat(selectedChat?.id, { chatGroups: groups });
            loadChatsCb();
        } catch (e) {
            const err = e as AxiosError;
            handleAssignError(err.response?.data?.error?.key);
        } finally {
            handleCloseModal();
        }
    };

    const handleCloseModal = () => {
        setSelectedGroupsId([]);
        setSelectedGroupsId([]);
        setAcceptButtonDisabled(true);
        handleCloseModalCb();
        setIsLoading(false);
    };

    const handleSelectGroupChange = (selectedGroupsId: number[]) => {
        setSelectedGroupsId(selectedGroupsId);
        setAcceptButtonDisabled(false);
    };

    if (isMobileView()) {
        return (
            <IonModal isOpen={visible}>
                <IonToolbar>
                    <IonButtons slot="start">
                        <IonButton onClick={handleCloseModal}>{t("close")}</IonButton>
                    </IonButtons>
                    <IonTitle>{t("modal.assignChatGroupTitleReduced")}</IonTitle>
                    <IonButtons slot="end">
                        <IonButton
                            disabled={acceptButtonDisabled}
                            strong={true}
                            onClick={() => alert("ESTA ACA")}
                        >
                            {t("accept")}
                            {isLoading && <IonSpinner />}
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
                <IonContent>
                    <IonItem>
                        <IonLabel>{t("filters.groups")}</IonLabel>
                        <IonSelect
                            multiple
                            placeholder={t("select")}
                            value={selectedGroupsId}
                            onIonChange={(e) => handleSelectGroupChange(e.detail.value)}
                        >
                            {Array.isArray(groups) &&
                                groups.map((group) => (
                                    <IonSelectOption key={group.id} value={group.id}>
                                        {group.name}
                                    </IonSelectOption>
                                ))}
                        </IonSelect>
                    </IonItem>
                </IonContent>
            </IonModal>
        );
    }
    return (
        <Modal
            title={t("modal.assignChatGroupTitle")}
            open={visible}
            onOk={handleModalOk}
            onCancel={handleCloseModal}
            footer={[
                <Button key="back" onClick={handleCloseModal}>
                    {t("cancel")}
                </Button>,
                <Button
                    disabled={acceptButtonDisabled}
                    key="submit"
                    type="primary"
                    onClick={handleModalOk}
                    loading={isLoading}
                >
                    {t("accept")}
                </Button>,
            ]}
        >
            <Select
                mode="multiple"
                style={{ width: "100%" }}
                value={selectedGroupsId}
                onChange={handleSelectGroupChange}
                placeholder={t("filters.selectGroups")}
                showSearch={true}
                filterOption={(input, option: any) =>
                    option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
            >
                {Array.isArray(groups) &&
                    groups.map((group) => (
                        <Option key={group.id} value={group.id}>
                            {group.name}
                        </Option>
                    ))}
            </Select>
        </Modal>
    );
}
